import { useMemo } from 'react';

/**
 * A star-shaped "NEW" badge for freshly-added event cards.
 *
 * Renders for `windowHours` (default 36h) after `createdAt`. Overhangs
 * the top-left corner of the parent card so it draws the eye without
 * fighting with existing top-row badges (venue / seeking / drivers).
 *
 * Sizes: 'md' (46px) for the full event cards, 'sm' (32px) for the
 * compact grid tiles. Uses a CSS clip-path star + gentle pulse — no
 * SVG dependency required.
 */
const NewBadge = ({ createdAt, windowHours = 36, size = 'md' }) => {
  const isNew = useMemo(() => {
    if (!createdAt) return false;
    const ts = Date.parse(createdAt);
    if (Number.isNaN(ts)) return false;
    return (Date.now() - ts) / 3600000 < windowHours;
  }, [createdAt, windowHours]);

  if (!isNew) return null;

  const dims = size === 'sm'
    ? { box: 32, font: 8, shadow: '0 2px 6px rgba(224,64,251,0.6)' }
    : { box: 46, font: 10, shadow: '0 3px 10px rgba(224,64,251,0.65)' };

  return (
    <div
      data-testid="event-new-badge"
      className="absolute top-2 left-2 z-20 pointer-events-none select-none"
      style={{
        width: dims.box,
        height: dims.box,
        filter: `drop-shadow(${dims.shadow})`,
      }}
    >
      {/* Star silhouette — 5-point clip-path filled with the RollM8s
          fuchsia/pink gradient. Rotates gently for a "just landed"
          feel that doesn't distract from the card image behind it. */}
      <div
        className="absolute inset-0 animate-[spin_8s_linear_infinite]"
        style={{
          background: 'linear-gradient(135deg, #f472b6 0%, #e040fb 45%, #a855f7 100%)',
          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        }}
      />
      {/* Static text sits on top so it stays readable while the star
          rotates behind it. */}
      <span
        className="absolute inset-0 flex items-center justify-center font-black tracking-wider text-white"
        style={{ fontSize: dims.font, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}
      >
        NEW
      </span>
    </div>
  );
};

export default NewBadge;
